import { Item } from '@/@types/cart'
import { OrdersnapShot } from '@/@types/order'

export interface Ga4PurchaseItem {
    item_id: string
    item_name: string
    item_category: string
    item_variant: string
    price: number
    quantity: number
}

export interface Ga4PurchasePayload {
    transaction_id: string
    value: number
    currency: string
    items: Ga4PurchaseItem[]
}

type TranslateFn = (key: string) => string

interface OrderForGa4 {
    id: number
    items: Item[]
    order_snap_shot: OrdersnapShot
}

function centsToDecimal(cents: number): number {
    return Number((cents / 100).toFixed(2))
}

export function buildGa4PurchasePayload(order: OrderForGa4, translate: TranslateFn): Ga4PurchasePayload | null {
    const snapshot = order.order_snap_shot
    const snapshotItems = snapshot?.items_data ?? []

    if (!snapshotItems.length) return null

    const itemsByTicketId = new Map(order.items.map((item) => [item.ticket_id, item]))

    const items: Ga4PurchaseItem[] = snapshotItems.map((snapshotItem) => {
        const orderItem = itemsByTicketId.get(snapshotItem.ticket_id)
        const ticketCategory = snapshotItem.ticket_category
        const workshopName = orderItem?.ticket?.workshop?.description

        return {
            item_id: String(snapshotItem.ticket_id),
            item_name: translate(ticketCategory),
            item_category: workshopName ?? translate(ticketCategory),
            item_variant: ticketCategory,
            price: centsToDecimal(snapshotItem.applied_price_cents),
            quantity: snapshotItem.quantity,
        }
    })

    if (!items.length) return null

    return {
        transaction_id: String(order.id),
        value: centsToDecimal(snapshot.total_cents ?? 0),
        currency: snapshot.currency ?? 'BRL',
        items,
    }
}

export function pushGa4PurchaseEvent(order: OrderForGa4, translate: TranslateFn): void {
    const payload = buildGa4PurchasePayload(order, translate)

    if (!payload || payload.value <= 0 || !payload.items.length) return

    const storageKey = `ga4-purchase-${payload.transaction_id}`

    if (window.sessionStorage?.getItem(storageKey)) return

    const analyticsWindow = window as Window & {
        dataLayer?: Record<string, unknown>[]
        gtag?: (...args: unknown[]) => void
    }

    const purchaseEvent = {
        transaction_id: payload.transaction_id,
        value: payload.value,
        currency: payload.currency,
        items: payload.items,
    }

    const dataLayer = analyticsWindow.dataLayer ?? []
    analyticsWindow.dataLayer = dataLayer

    dataLayer.push({ ecommerce: null })
    dataLayer.push({
        event: 'purchase',
        ecommerce: purchaseEvent,
    })

    if (typeof analyticsWindow.gtag === 'function') {
        analyticsWindow.gtag('event', 'purchase', purchaseEvent)
    }

    window.sessionStorage?.setItem(storageKey, '1')
}
